/**
 * Attribute Normalizer
 * 
 * Converts component attributes between different structures to ensure consistency.
 * 
 * Problem: Some components use flat structure (paddingTop, paddingBottom, etc.)
 *          while others use nested structure (padding: {top, bottom, left, right})
 * 
 * Solution: This utility normalizes all structures to the flat format that
 *           CommonAttributesForm expects.
 */

/**
 * Normalize common attributes to flat structure
 * @param {Object} attrs - Component's commonAttrs object
 * @returns {Object} Normalized flat structure
 */
export const normalizeCommonAttrs = (attrs) => {
  if (!attrs) {
    return {
      width: '100',
      height: 'auto',
      paddingTop: '16',
      paddingBottom: '16',
      paddingLeft: '16',
      paddingRight: '16',
      marginTop: '8',
      marginBottom: '8',
      marginLeft: '0',
      marginRight: '0'
    };
  }

  // If already in flat structure, return as is
  if (attrs.paddingTop !== undefined) {
    return { ...attrs };
  }

  // If in nested structure, convert to flat
  if (attrs.padding?.top !== undefined || attrs.margin?.top !== undefined) {
    const padding = attrs.padding || {};
    const margin = attrs.margin || {};

    return {
      ...attrs,
      // Convert padding
      paddingTop: String(padding.top ?? attrs.paddingTop ?? '16'),
      paddingBottom: String(padding.bottom ?? attrs.paddingBottom ?? '16'),
      paddingLeft: String(padding.left ?? attrs.paddingLeft ?? '16'),
      paddingRight: String(padding.right ?? attrs.paddingRight ?? '16'),
      
      // Convert margin
      marginTop: String(margin.top ?? attrs.marginTop ?? '8'),
      marginBottom: String(margin.bottom ?? attrs.marginBottom ?? '8'),
      marginLeft: String(margin.left ?? attrs.marginLeft ?? '0'),
      marginRight: String(margin.right ?? attrs.marginRight ?? '0'),
      
      // Remove nested objects to avoid confusion
      padding: undefined,
      margin: undefined
    };
  }

  // Fallback: return with defaults
  return {
    width: attrs.width || '100',
    height: attrs.height || 'auto',
    paddingTop: '16',
    paddingBottom: '16',
    paddingLeft: '16',
    paddingRight: '16',
    marginTop: '8',
    marginBottom: '8',
    marginLeft: '0',
    marginRight: '0',
    ...attrs
  };
};

/**
 * Convert flat structure to nested structure
 * (Use only if you want to standardize to nested format)
 * @param {Object} attrs - Flat commonAttrs object
 * @returns {Object} Nested structure
 */
export const flatToNested = (attrs) => {
  if (!attrs) return {};

  // If already nested, return as is
  if (attrs.padding?.top !== undefined) {
    return { ...attrs };
  }

  // Convert flat to nested
  return {
    ...attrs,
    padding: {
      top: Number(attrs.paddingTop) || 16,
      bottom: Number(attrs.paddingBottom) || 16,
      left: Number(attrs.paddingLeft) || 16,
      right: Number(attrs.paddingRight) || 16
    },
    margin: {
      top: Number(attrs.marginTop) || 8,
      bottom: Number(attrs.marginBottom) || 8,
      left: Number(attrs.marginLeft) || 0,
      right: Number(attrs.marginRight) || 0
    },
    // Remove flat properties
    paddingTop: undefined,
    paddingBottom: undefined,
    paddingLeft: undefined,
    paddingRight: undefined,
    marginTop: undefined,
    marginBottom: undefined,
    marginLeft: undefined,
    marginRight: undefined
  };
};

/**
 * Get padding/margin value safely from either structure
 * @param {Object} attrs - Component's commonAttrs
 * @param {string} property - Property name (e.g., 'paddingTop' or 'marginBottom')
 * @returns {string} Value as string
 */
export const getAttrValue = (attrs, property) => {
  if (!attrs) return '0';

  // Try flat structure first
  if (attrs[property] !== undefined) {
    return String(attrs[property]);
  }

  // Try nested structure
  const [type, side] = property.match(/padding|margin|(\w+)/) || [];
  if (type && side && attrs[type]?.[side.toLowerCase()] !== undefined) {
    return String(attrs[type][side.toLowerCase()]);
  }

  // Defaults
  const defaults = {
    paddingTop: '16',
    paddingBottom: '16',
    paddingLeft: '16',
    paddingRight: '16',
    marginTop: '8',
    marginBottom: '8',
    marginLeft: '0',
    marginRight: '0'
  };

  return defaults[property] || '0';
};

/**
 * Apply normalized attributes as inline styles
 * @param {Object} attrs - Component's commonAttrs
 * @returns {Object} React style object
 */
export const attrsToStyle = (attrs) => {
  const normalized = normalizeCommonAttrs(attrs);

  return {
    width: normalized.width === 'auto' ? 'auto' : `${normalized.width}%`,
    height: normalized.height === 'auto' ? 'auto' : `${normalized.height}px`,
    paddingTop: `${normalized.paddingTop}px`,
    paddingBottom: `${normalized.paddingBottom}px`,
    paddingLeft: `${normalized.paddingLeft}px`,
    paddingRight: `${normalized.paddingRight}px`,
    marginTop: `${normalized.marginTop}px`,
    marginBottom: `${normalized.marginBottom}px`,
    marginLeft: `${normalized.marginLeft}px`,
    marginRight: `${normalized.marginRight}px`
  };
};

/**
 * Debug helper: Log component attributes structure
 * @param {Object} component - Component object
 * @param {string} label - Optional label for logging
 */
export const debugAttributes = (component, label = 'Component') => {
  console.group(`🔍 ${label} Attributes Debug`);
  console.log('Component ID:', component.id);
  console.log('Component Type:', component.type);
  console.log('Common Attrs (raw):', component.commonAttrs);
  
  const normalized = normalizeCommonAttrs(component.commonAttrs);
  console.log('Common Attrs (normalized):', normalized);
  
  const isFlat = component.commonAttrs?.paddingTop !== undefined;
  const isNested = component.commonAttrs?.padding?.top !== undefined;
  
  console.log('Structure:', {
    isFlat,
    isNested,
    isMixed: isFlat && isNested,
    isUnknown: !isFlat && !isNested
  });
  
  console.groupEnd();
};

export default {
  normalizeCommonAttrs,
  flatToNested,
  getAttrValue,
  attrsToStyle,
  debugAttributes
};


